import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";
import { BLOG_POSTS } from "@/data/blogPosts";

const SITE_URL = "https://deepcleanrr.com";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogIndexPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Deep Clean Restroom Services Blog",
    url: `${SITE_URL}/blog`,
    publisher: {
      "@type": "Organization",
      name: "Deep Clean Restroom Services",
      url: SITE_URL,
    },
    blogPost: BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE_URL}/blog/${p.slug}`,
      datePublished: p.date,
      image: p.coverImage,
    })),
  };

  return (
    <main data-testid="blog-index-page" className="bg-white">
      <SEO
        title="Blog | Deep Clean Restroom Services — Wilmington, NC"
        description="Insights, tips, and best practices on commercial restroom sanitation, deep-foam cleaning, and facility hygiene from Wilmington's restroom cleaning specialists."
        canonical={`${SITE_URL}/blog`}
        image={BLOG_POSTS[0]?.coverImage}
        type="website"
        jsonLd={jsonLd}
        keywords={[
          "commercial restroom cleaning blog",
          "Wilmington restroom sanitation",
          "deep foam cleaning tips",
          "facility hygiene",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/u23f4tmk_North Carolina%20Restroom%20Cleaning.png')",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.92) 0%, rgba(5,9,19,0.70) 30%, rgba(5,9,19,0.55) 100%), linear-gradient(90deg, rgba(5,9,19,0.85) 0%, rgba(5,9,19,0.50) 55%, rgba(5,9,19,0.20) 100%), radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.25), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-20 pt-16 md:px-12 md:pb-28 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            — The Deep Clean Journal
          </p>
          <h1
            data-testid="blog-index-heading"
            className="max-w-4xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl"
          >
            Restroom sanitation
            <br />
            <span className="text-cyan-300">insights & best practices.</span>
          </h1>
          <p className="mt-6 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-300 md:text-lg">
            Practical guides on commercial restroom cleaning, deep-foam
            sanitation, odor control, and facility hygiene — written for Wilmington
            property managers, owners, and operations leads.
          </p>
        </div>
      </section>

      {/* POSTS */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((post, i) => (
              <article
                key={post.slug}
                data-testid={`blog-card-${i}`}
                className="group flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white transition-colors hover:border-blue-600"
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={post.coverImage}
                      alt={post.coverAlt || post.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                </Link>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <div className="mb-4 flex items-center gap-4 font-body text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <span className="text-blue-600">{post.category}</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} strokeWidth={2} />
                      {formatDate(post.date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={12} strokeWidth={2} />
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="transition-colors hover:text-blue-600"
                    >
                      {post.title}
                    </Link>
                  </h2>
                  <p className="mt-3 line-clamp-3 font-body text-sm font-medium leading-relaxed text-slate-600">
                    {post.description}
                  </p>
                  <Link
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-card-link-${i}`}
                    className="mt-6 inline-flex items-center gap-1.5 font-body text-sm font-bold text-blue-600 hover:underline"
                  >
                    Read article
                    <ArrowRight size={14} strokeWidth={2.2} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogIndexPage;
